import React, { useCallback, useState } from 'react';
import { v4 as UUIDV4 } from 'uuid';
import ReactDOM from 'react-dom';
import DisplaySuccess from './DisplaySuccess';
import { InformationFilled16 } from '@carbon/icons-react';
import { EmptyStatePage } from 'components';
import { Integration } from '@carbon/pictograms-react';

import {
  DataTable,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableContainer,
  TableToolbar,
  TableToolbarContent
} from 'carbon-components-react';

import { fetchApiKeysStart } from 'redux/user/actions';
import { toLocalDateTimeFormat } from 'utilities/helper.util';
import { Button } from 'carbon-components-react';
import { useDispatch, useSelector } from 'react-redux';
import { Header } from 'redux/member/types';
import { Add16 } from '@carbon/icons-react';
import DisplayApiKeysRow from './DisplayApiKeysRow';
import Services from 'services';
import { addAlert, removeAlert } from 'redux/alerts/actions';
import { APP } from 'app/AppRoutes';
import { selectAlertsData } from 'redux/alerts';
import { useComponentMount } from 'utilities/hooks.util';

const uniqueId = UUIDV4();

const DisplayApiKeys: React.FC<any> = ({ apiKeys }) => {
  const dispatch: any = useDispatch();
  const alertList = useSelector(selectAlertsData);
  const [isGeneratingKeys, setIsGeneratingKeys] = useState<boolean>(false);
  const headers: Array<Header> = [
    {
      key: 'clientId',
      header: 'Client ID'
    },
    { key: 'clientSecret', header: 'Client secret' },
    { key: 'createdAt', header: 'Created' },
    { key: 'lastUsedAt', header: 'Last used' }
  ];
  apiKeys = apiKeys?.map((apikey: any) => ({
    clientSecret: '*********',
    lastUsedAt:
      (apikey.lastUsedAt && toLocalDateTimeFormat(apikey.lastUsedAt)) ||
      'Never',
    createdAt: toLocalDateTimeFormat(apikey.createdAt),
    id: apikey.id.toString(),
    clientId: apikey.clientId
  }));

  const [newKeys, setNewKeys] = useState<any>(null);

  const deleteAlertActive = !!alertList.find(
    (alert: any) => alert.notificationProps.title === 'Delete API key'
  );

  const notificationProps = {
    title: 'Sorry, two keys maximum',
    subtitle:
      'To generate a new API key you must first delete one of your existing ones',
    lowContrast: true,
    doActions: null,
    actionButtonText: null
  };
  const handleClick = async () => {
    if (isGeneratingKeys) return;
    setIsGeneratingKeys(true);
    if (apiKeys?.length >= 2) {
      dispatch(
        addAlert('DELETE_USER_APIKEY_GROUP', 'error', '', '', {
          id: uniqueId,
          withClose: false,
          type: 'inline',
          notificationProps
        })
      );
      setIsGeneratingKeys(false);
      return true;
    }
    try {
      const response: any = await Services.user.generateApiKeyForUser();
      setNewKeys({
        clientId: response.clientId,
        clientSecret: response.clientSecret
      });
    } catch (error) {
      const notificationProps = {
        title: 'Generate api failed',
        lowContrast: true
      };
      dispatch(
        addAlert('DELETE_USER_APIKEY_GROUP', 'error', '', '', {
          withClose: false,
          type: 'inline',
          notificationProps
        })
      );
    } finally {
      setIsGeneratingKeys(false);
    }
  };
  const handleDone = () => {
    dispatch(fetchApiKeysStart());
    setNewKeys(null);
  };

  const removeReachedLimitAlert = useCallback(() => {
    if (
      apiKeys?.length < 2 &&
      !!alertList.find((alertItem: any) => alertItem.id === uniqueId)
    ) {
      dispatch(removeAlert(uniqueId));
    }
  }, [apiKeys, alertList, dispatch]);

  useComponentMount(() => {
    removeReachedLimitAlert();
  });

  return (
    <>
      {apiKeys?.length > 0 ? (
        <>
          <div className="apikey-data-table">
            <DataTable rows={apiKeys} headers={headers}>
              {({
                rows,
                headers,
                getTableProps,
                getHeaderProps,
                getRowProps
              }: any) => {
                return (
                  <TableContainer>
                    <TableToolbar>
                      <TableToolbarContent>
                        <Button
                          onClick={handleClick}
                          renderIcon={Add16}
                          iconDescription="Generate API key"
                          disabled={deleteAlertActive}
                        >
                          Generate API key
                        </Button>
                      </TableToolbarContent>
                    </TableToolbar>
                    <Table {...getTableProps()}>
                      <TableHead>
                        <TableRow>
                          {headers.map((header: any) => (
                            <TableHeader
                              key={header.key}
                              {...getHeaderProps({
                                header
                              })}
                            >
                              {header.header}
                            </TableHeader>
                          ))}
                          <TableHeader></TableHeader>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {rows.map((row: any, index: any) => (
                          <DisplayApiKeysRow
                            row={row}
                            getRowProps={getRowProps}
                            key={index}
                          />
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                );
              }}
            </DataTable>
          </div>
          <hr className="section--divider" />
          <div className="apikey-table-footer">
            <InformationFilled16 />
            {'API key client secrets are only displayed when first created'}
          </div>
        </>
      ) : (
        <EmptyStatePage>
          <div className="apikeys-no-data--wrapper">
            <Integration />
            <div className="apikeys-empty-state-message">
              There are no API keys to display
            </div>
            <div
              title="Generate API key"
              className="apikeys-empty-state-button"
              onClick={handleClick}
            >
              Generate API key
            </div>
          </div>
        </EmptyStatePage>
      )}
      {newKeys &&
        ReactDOM.createPortal(
          <DisplaySuccess apikey={newKeys} handleDone={handleDone} />,
          document.querySelector(`#${APP}`) || document.body
        )}
    </>
  );
};
DisplayApiKeys.displayName = 'DisplayApiKeys';
export default DisplayApiKeys;
